<template>
    <div class="edit-building">
        <!-- Building Header -->
        <detail-page-header
            :title="building.address_line_1"
            :subtitle="`${building.city}, ${building.state}`"
            :show-back-button="!isModal && !!fromSiteId"
            :title-clickable="!isModal"
            :show-title-chevron="!isModal"
            :show-close-button="isModal"
            :is-modal="isModal"
            :draggable="isModal"
            :title-large="isModal"
            back-button-label="Go back to site"
            title-click-label="View all buildings"
            close-button-label="Close panel"
            @back="goBackToSite"
            @title-click="showBuildings"
            @close="$emit('close')"
        >
            <template #actions>
                <themed-dropdown v-if="!isModal && $gate.isAdmin()" label="Manage Building" :actions="manageBuildingActions" @action="handleManageBuildingAction" />
            </template>
        </detail-page-header>

        <!-- Building Content -->
        <div 
            id="building-container"
            class="edit-building__content" 
            :class="{ 'edit-building__content--modal': isModal }"
        >
            <!-- Media Gallery and Map Section -->
            <div class="edit-building__media-section">
                <div class="edit-building__gallery">
                    <media-gallery-v1
                        :documentable-id="building.id"
                        documentable-type="App\Building"
                        :media="building.media"
                        :height="500"
                        :object-cover="true"
                    />
                </div>
                
                <div class="edit-building__map">
                    <map-component
                        :latitude="building.latitude"
                        :longitude="building.longitude"
                        :zoom="6"
                    />
                </div>
            </div>

            <!-- Tabs Section -->
            <div class="edit-building__tabs-wrapper">
                <div v-if="isModal" class="edit-building__tab-select">
                    <label for="section-select" class="edit-building__tab-select-label">Viewing Section</label>
                    <select 
                        id="section-select"
                        v-model="selectedTab" 
                        class="edit-building__tab-select-input"
                        aria-label="Select section to view"
                    >
                        <option 
                            v-for="(tab, slug) in enabledTabs" 
                            :key="slug"
                            :value="slug"
                        >
                            {{ tab.label }}
                        </option>
                    </select>
                </div>
                
                <nav v-else class="edit-building__tabs" aria-label="Building sections">
                    <button
                        v-for="(tab, slug) in enabledTabs"
                        :key="slug"
                        @click="selectedTab = slug"
                        class="edit-building__tab"
                        :class="{ 
                            'edit-building__tab--active': slug === selectedTab,
                            [tab.customClass]: tab.customClass
                        }"
                        :aria-selected="slug === selectedTab"
                        :aria-label="`View ${tab.label} section`"
                        role="tab"
                    >
                        {{ tab.label }}
                        <span
                            v-if="tab.showCount"
                            class="edit-building__tab-count"
                            :class="{ 'edit-building__tab-count--active': slug === selectedTab }"
                        >
                            {{ tab.count }}
                        </span>
                    </button>
                </nav>
            </div>

            <!-- Tab Content -->
            <div class="edit-building__tab-content" :class="{ 'edit-building__tab-content--bordered': !isModal }">
                <!-- Combined Building Info Tab -->
                <div v-if="selectedTab === 'buildingInfo'" class="edit-building__combined-tab">
                    <!-- Two Column Layout -->
                    <div class="edit-building__two-column-layout">
                        <!-- Left Column: Address, Geolocation, Danger Zone -->
                        <div class="edit-building__left-column">
                            <div class="edit-building__section">
                                <edit-address :building-data="building" />
                            </div>

                            <div class="edit-building__section">
                                <div class="edit-building__box">
                                    <div class="edit-building__box-header section-box-header">
                                        <h3 class="edit-building__box-title section-box-title">Danger Zone</h3>
                                    </div>
                                    <div class="edit-building__box-content">
                                        <building-danger-zone 
                                            :building-data="building"
                                            :view-only-mode="false"
                                            :hide-title="true"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Right Column: Building Type & Details Combined -->
                        <div class="edit-building__right-column">
                            <div class="edit-building__section">
                                <div class="edit-building__box">
                                    <div class="edit-building__box-header section-box-header">
                                        <h3 class="edit-building__box-title section-box-title">Building Type</h3>
                                    </div>
                                    <div class="edit-building__box-content">
                                        <edit-building-type :building-data="building" />
                                        
                                        <div class="edit-building__details-section">
                                            <h4 class="edit-building__details-title section-subtitle">Details</h4>
                                            <edit-building-details :building-data="building" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <units 
                    v-if="selectedTab === 'units'" 
                    :show-edit-actions="true"
                />

                <contacts
                    v-if="selectedTab === 'contacts'"
                    :contact-data="building.contacts"
                    attach-to="building"
                    :attach-to-id="building.id"
                />

                <documents
                    v-if="selectedTab === 'documents'"
                    :document-data="building.documents"
                    :documentable-id="building.id"
                    documentable-type="App\Building"
                />

                <!-- Save Button -->
                <div v-if="showSaveButton" class="edit-building__save-section">
                    <themed-button 
                        @click="saveBuilding" 
                        variant="default"
                        size="lg"
                    >
                        Save Building
                    </themed-button>
                </div>
            </div>
        </div>

        <!-- Modals -->
        <notifications classes="notification" position="bottom right" />

        <modal 
            name="building-cards" 
            width="1300px" 
            height="auto" 
            click-to-close 
            scrollable 
            adaptive
        >
            <div class="edit-building__modal-content">
                <building-cards
                    view="table"
                    :per-page="7"
                    :show-create-new="false"
                    :show-client-info="false"
                    small-header
                    center-cards
                />
            </div>
        </modal>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import EditAddress from './edit/EditAddress.vue';
    import EditBuildingType from './edit/EditBuildingType.vue';
    import EditBuildingDetails from './edit/EditBuildingDetails.vue';
    import BuildingDangerZone from '../sites/BuildingDangerZone.vue';
    import Units from '../units/Units.vue';
    import Contacts from '../contacts/Contacts.vue';
    import Documents from '../documents/Documents.vue';
    import ThemedDropdown from '../ThemedDropdown.vue';
    import ThemedButton from '../ThemedButton.vue';
    import DetailPageHeader from '../DetailPageHeader.vue';
    import MediaGalleryV1 from '../MediaGallery.vue';

    export default {
        name: 'EditBuilding',
        
        components: {
            EditAddress,
            EditBuildingType,
            EditBuildingDetails,
            BuildingDangerZone,
            Units,
            Contacts,
            Documents,
            ThemedDropdown,
            ThemedButton,
            DetailPageHeader,
            MediaGalleryV1
        },
        
        props: {
            buildingData: {
                type: Object,
                required: true
            },

            openTab: {
                type: String,
                default: 'buildingInfo'
            },

            isModal: {
                type: Boolean,
                default: false
            },

            fromSiteId: {
                type: Number,
                default: null
            }
        },

        data() {
            return {
                selectedTab: this.openTab
            }
        },

        created() {
            this.buildings.selectedBuilding = this.buildingData;
            this.documents.all = this.buildingData.documents;
            this.documents.filtered = this.buildingData.documents;
            this.units.all = this.buildingData.units;
            this.contacts.all = this.buildingData.contacts;
            this.contacts.filtered = this.buildingData.contacts;
        },

        computed: {
            enabledTabs() {
                // v-for used to be paired with v-if="tab.enabled"; Vue 2 runs
                // v-for first so the list was already filtered. Same result, and
                // it does not break when the directive priority reverses in Vue 3.
                const source = this.tabs;
                return Array.isArray(source)
                    ? source.filter(entry => entry.enabled)
                    : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
            },

            ...mapState(['buildings', 'documents', 'units', 'contacts']),

            building() {
                return this.buildings.selectedBuilding;
            },

            showSaveButton() {
                return this.tabs[this.selectedTab]?.showSaveButton || false;
            },

            tabs() {
                return {
                    buildingInfo: {
                        label: "Building Info",
                        enabled: true,
                        showCount: false,
                        showSaveButton: true,
                        customClass: this.building.is_in_danger_zone ? 'edit-building__tab--danger' : ''
                    },
                    units: {
                        label: "Units",
                        enabled: true,
                        showCount: true,
                        count: this.building.units?.length || 0,
                        showSaveButton: false
                    },
                    contacts: {
                        label: "Contacts",
                        enabled: true,
                        showCount: true,
                        count: this.contacts.all?.length || 0,
                        showSaveButton: false
                    },
                    documents: {
                        label: "Documents",
                        enabled: true,
                        showCount: true,
                        count: this.documents.all?.length || 0,
                        showSaveButton: false
                    }
                }
            },

            manageBuildingActions() {
                const actions = [];
                
                if (this.$gate.isAdmin()) {
                    actions.push({
                        label: 'Delete Building',
                        value: 'delete',
                        icon: 'trash',
                        variant: 'destructive'
                    });
                }
                
                return actions;
            }
        },

        methods: {
            showBuildings() {
                this.$modal.show('building-cards');
            },

            goBackToSite() {
                window.location.href = `/sites/${this.fromSiteId}`;
            },

            handleManageBuildingAction(action) {
                if (action === 'delete') {
                    this.confirmDeleteBuilding();
                }
            },

            saveBuilding() {
                axios.patch('/buildings/' + this.building.id, this.building)
                    .then(response => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Building Saved'
                        });
                    })
                    .catch(error => {
                        console.error(error);
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: 'Failed to save building'
                        });
                    });
            },

            confirmDeleteBuilding() {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this building?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteBuilding();
                    }
                });
            },

            deleteBuilding() {
                axios.delete('/buildings/' + this.building.id)
                    .then(response => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Building Deleted'
                        });

                        setTimeout(() => {
                            window.location = '/admin/buildings';
                        }, 1000);
                    })
                    .catch(error => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    });
            }
        }
    }
</script>
